import { useEffect, useState } from "react";
import { getEmailsForTab, hasEmailAccess, subscribeEmailAccess } from "./emailAccessConfig";

/**
 * Only these roles are allowed to access the admin dashboard.
 * Must match backend minRoles. If profile API returns a role not in this list, user is logged out immediately.
 */
export const ALLOWED_ADMIN_ROLES = [
  "ROLE_ADMIN",
  "ROLE_ADMIN_TEACHERADMIN",
  "ROLE_ADMIN_MARKETING",
  "ROLE_ADMIN_FINANCE",
  "ROLE_ADMIN_ACADEMICS",
  "ROLE_ADMIN_OPERATIONS",
  "ROLE_ADMIN_COUNSELLOR",
  "ROLE_ADMIN_DEMOOPS_AGENT",
  "ROLE_ADMIN_TEACHER",
  "ROLE_ADMIN_TEACHER_STUDENT",
  "ROLE_TEACHER_LEADER",
  "ROLE_OUT_SOURCING",
  // Sales: SALES_ADMIN sees every agent and manages targets; SALES_AGENT sees
  // only their own performance (artium-reports enforces both).
  "ROLE_SALES_ADMIN",
  "ROLE_SALES_AGENT",
] as const;

const SALES_ROLES = ["ROLE_SALES_ADMIN", "ROLE_SALES_AGENT"] as const;

/**
 * Single switch for Teacher Leader UI restrictions.
 * - teacherLeaderDisabled: true  → restrict/disable TL actions & menus
 * - teacherLeaderDisabled: false → treat TL like a normal admin (restrictions off)
 *
 * Update this one key to turn all TL restrictions on/off across the app.
 */
export const TEACHER_LEADER_CONFIG = {
  teacherLeaderDisabled: true,
} as const;

/** Normalize role strings from API/localStorage (adds ROLE_ prefix when missing). */
export const normalizeRole = (role: string | undefined | null): string => {
  if (!role || typeof role !== "string") return "";
  let normalized = role.trim().toUpperCase();
  if (!normalized.startsWith("ROLE_")) {
    normalized = "ROLE_" + normalized;
  }
  return normalized;
};

/** Flatten role / authorities from a string, comma-separated string, or array. */
export const toRoleList = (roleOrRoles: unknown): string[] => {
  if (roleOrRoles == null) return [];
  const values = Array.isArray(roleOrRoles)
    ? roleOrRoles
    : typeof roleOrRoles === "string"
      ? roleOrRoles.split(",")
      : [];
  return values
    .map((role) => normalizeRole(typeof role === "string" ? role : String(role ?? "")))
    .filter(Boolean);
};

/**
 * API may return a single role, a comma-separated string, or an authorities array.
 * Allow access if ANY role matches ALLOWED_ADMIN_ROLES.
 */
export const isAllowedAdminRoleFromProfile = (role: unknown): boolean => {
  const allowed = ALLOWED_ADMIN_ROLES as readonly string[];
  return toRoleList(role).some((normalized) => allowed.includes(normalized));
};

/** Prefer an allowed admin role when multiple authorities are present. */
export const pickPrimaryAdminRole = (role: unknown): string => {
  const roles = toRoleList(role);
  const allowed = ALLOWED_ADMIN_ROLES as readonly string[];
  return roles.find((normalized) => allowed.includes(normalized)) || roles[0] || "";
};

/** True for ROLE_TEACHER_LEADER / TEACHER_LEADER. */
export const isTeacherLeaderRole = (role: string | undefined | null): boolean =>
  normalizeRole(role) === "ROLE_TEACHER_LEADER";

/** Reads profileData / userProfileData from localStorage and checks Teacher Leader. */
export const isTeacherLeader = (): boolean => {
  try {
    const raw = localStorage.getItem("profileData");
    if (raw) {
      const { role } = JSON.parse(raw);
      if (toRoleList(role).some((item) => isTeacherLeaderRole(item))) return true;
    }
    const loginRaw = localStorage.getItem("userProfileData");
    if (loginRaw) {
      const data = JSON.parse(loginRaw);
      if (toRoleList(data?.role).some((item) => isTeacherLeaderRole(item))) return true;
      if (toRoleList(data?.authorities).some((item) => isTeacherLeaderRole(item))) return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Use this everywhere UI should be disabled for Teacher Leaders.
 * Returns true only when the user is a TL AND TEACHER_LEADER_CONFIG.teacherLeaderDisabled is true.
 * Flip the config key to false to re-enable everything for TLs in one place.
 */
export const isTeacherLeaderDisabled = (): boolean =>
  TEACHER_LEADER_CONFIG.teacherLeaderDisabled && isTeacherLeader();

/** Fired after profileData is written so menus can re-evaluate TL restrictions. */
export const PROFILE_DATA_UPDATED_EVENT = "profileDataUpdated";

export const notifyProfileDataUpdated = (): void => {
  try {
    window.dispatchEvent(new Event(PROFILE_DATA_UPDATED_EVENT));
  } catch {
    // no-op
  }
};

/** Teacher-leader APIs expect profile `userId` (profileAPI id), not login credentialId. */
export const getTeacherLeaderUserId = (): string | null => {
  const fromObject = (data: any): string | null => {
    if (!data || typeof data !== "object") return null;
    if (data.userId != null && data.userId !== "") return String(data.userId);
    if (data.uId != null && data.uId !== "") return String(data.uId);
    if (data.id != null && data.id !== "") return String(data.id);
    return null;
  };
  try {
    const profileRaw = localStorage.getItem("profileData");
    if (profileRaw) {
      const fromProfile = fromObject(JSON.parse(profileRaw));
      if (fromProfile) return fromProfile;
    }
    const loginRaw = localStorage.getItem("userProfileData");
    if (loginRaw) {
      const fromLogin = fromObject(JSON.parse(loginRaw));
      if (fromLogin) return fromLogin;
    }
  } catch {
    // no-op
  }
  return null;
};

/** Student Profile tabs Teacher Leaders may open (view-only where applicable). */
export const TEACHER_LEADER_STUDENT_TABS = [
  "Profile",
  "Calendar",
  "Class History",
  "Class Details",
  "KYS Form",
] as const;

/** Teacher Profile tabs Teacher Leaders may open (view-only; actions disabled inside). */
export const TEACHER_LEADER_TEACHER_TABS = [
  "Profile",
  "Student List",
  "Paid Calendar",
] as const;

/** Main sidebar top-level titles Teacher Leaders may access. */
export const TEACHER_LEADER_MAIN_MENU_TITLES = [
  "Profiles",
  "User Profile",
  "Teacher Operations",
] as const;

/** Sidebar child links Teacher Leaders may open. */
export const TEACHER_LEADER_ALLOWED_SIDEBAR_CHILDREN = [
  "Teacher",
  "Teacher Reports",
] as const;

/** Sidebar child links Teacher Leaders must not open (use Teacher → Student List instead). */
export const TEACHER_LEADER_BLOCKED_SIDEBAR_CHILDREN = ["Student"] as const;

/** True when a sidebar / profile-tab title is allowed for a restricted TL. */
export const isTeacherLeaderTabAllowed = (
  title: string | undefined | null,
  allowedTitles: readonly string[]
): boolean => {
  if (!title) return false;
  const normalized = title.trim().toLowerCase();
  return allowedTitles.some((t) => t.toLowerCase() === normalized);
};

/**
 * Clears all auth-related data from localStorage and cookie.
 * Use on logout and when denying access (e.g. missing or invalid token).
 */
export const clearAuthStorage = (): void => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("login");
  localStorage.removeItem("loginDetails");
  localStorage.removeItem("loginUser");
  localStorage.removeItem("profileData");
  localStorage.removeItem("credentialId");
  localStorage.removeItem("userId");
  // The telephony identity, and why a sign-out has to name it explicitly:
  // userProfileData is the whole login response, and the agent's `uId` is
  // persisted nowhere else, so TelephonyProvider.currentAgentId() kept
  // resolving after logout. The softphone then re-initialised as the agent who
  // had just signed out — visible on the login page, registered, reachable for
  // inbound calls and able to dial. telephonyAgentId is the manual override
  // for the same value and would do exactly the same, so it goes too.
  localStorage.removeItem("userProfileData");
  localStorage.removeItem("telephonyAgentId");
  document.cookie = "jwtToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.artiumacademy.com;";
  document.cookie = "jwtToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
};

/** Clears auth storage and redirects to login page (full window navigation). */
export const clearAuthAndRedirectToLogin = (): void => {
  clearAuthStorage();
  const base = process.env.PUBLIC_URL || "";
  window.location.href = `${base}/login`;
};

export const hasFinanceAccess = (): boolean => {
  return true;
};

export const TELEPHONY_ONLY_HOME = `${process.env.PUBLIC_URL}/pages/telephony-logs`;

/** Logged-in admin email from profileData, falling back to the OTP login response. */
const getLoggedInEmail = (): string => {
  try {
    const profile = JSON.parse(localStorage.getItem("profileData") || "{}");
    const login = JSON.parse(localStorage.getItem("userProfileData") || "{}");
    return String(profile?.emailId || login?.emailId || login?.email || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

/**
 * Emails on the "telephonyOnly" list in adminEmailAccess.json may use the Telephony
 * pages only — every other tab is hidden and any other URL redirects to
 * TELEPHONY_ONLY_HOME. Stopgap until a dedicated backend role exists: these users
 * still hold a normal admin role, so this gates UI only (same caveat as
 * Transactions/Rnr/TeacherChange lists).
 *
 * Pass `email` at login time, before profileData has been fetched (e.g. the typed login id).
 */
export const isTelephonyOnlyUser = (email?: string): boolean => {
  const candidates = [getLoggedInEmail(), String(email || "").trim().toLowerCase()];
  const telephonyOnlyEmails = getEmailsForTab("telephonyOnly");
  return candidates.some((e) => !!e && telephonyOnlyEmails.includes(e));
};

/**
 * Pages telephony-only users may open: Logs (incl. /:callId detail), Countries,
 * and the Dialer route that dial links / the floating bubble use.
 */
const TELEPHONY_ONLY_PATHS = ["telephony-logs", "telephony-countries", "telephony-dialer"].map(
  (page) => `${process.env.PUBLIC_URL}/pages/${page}`
);

export const isTelephonyOnlyPath = (pathname: string): boolean =>
  TELEPHONY_ONLY_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * Re-evaluates isTelephonyOnlyUser when profileData arrives (mobile-number logins only know the email then)
 * and when adminEmailAccess.json finishes loading.
 */
export const useTelephonyOnlyUser = (): boolean => {
  const [telephonyOnly, setTelephonyOnly] = useState(isTelephonyOnlyUser());
  useEffect(() => {
    const refresh = () => setTelephonyOnly(isTelephonyOnlyUser());
    window.addEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
    const unsubscribe = subscribeEmailAccess(refresh);
    refresh();
    return () => {
      window.removeEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
      unsubscribe();
    };
  }, []);
  return telephonyOnly;
};

/** Outsourced agents (ROLE_OUT_SOURCING) land on Student profile; their sidebar/routes are limited below. */
export const OUT_SOURCING_HOME = `${process.env.PUBLIC_URL}/pages/student-profile`;

/** Sidebar Main-menu → children outsourced agents may see. */
export const OUT_SOURCING_MENU_ITEMS: Record<string, readonly string[]> = {
  Profiles: ["Student"],
  Telephony: ["Logs", "Countries"],
};

/** True when profileData / login payload carries ROLE_OUT_SOURCING. */
export const isOutSourcingUser = (): boolean => {
  try {
    const profile = JSON.parse(localStorage.getItem("profileData") || "{}");
    const login = JSON.parse(localStorage.getItem("userProfileData") || "{}");
    return [profile?.role, login?.role, login?.authorities]
      .flatMap((value) => toRoleList(value))
      .includes("ROLE_OUT_SOURCING");
  } catch {
    return false;
  }
};

const getStoredRoles = (): string[] => {
  try {
    const profile = JSON.parse(localStorage.getItem("profileData") || "{}");
    const login = JSON.parse(localStorage.getItem("userProfileData") || "{}");
    return [profile?.role, login?.role, login?.authorities].flatMap((value) => toRoleList(value));
  } catch {
    return [];
  }
};

/**
 * SALES_ADMIN / SALES_AGENT from the login API. Restricted only when they have no
 * other allowed admin role, so a real admin who also carries a sales role keeps full access.
 */
export const isSalesRestrictedUser = (): boolean => {
  const roles = getStoredRoles();
  const hasSales = roles.some((role) => (SALES_ROLES as readonly string[]).includes(role));
  if (!hasSales) return false;
  const allowed = ALLOWED_ADMIN_ROLES as readonly string[];
  return !roles.some(
    (role) => allowed.includes(role) && !(SALES_ROLES as readonly string[]).includes(role)
  );
};

/**
 * Sales agent without SALES_ADMIN or the plain Admin role.
 * Those accounts hide Sales Agents and Targets & Incentives.
 * ROLE_ADMIN keeps those screens even if a sales-agent authority is also present.
 */
export const isSalesAgentUser = (): boolean => {
  const roles = getStoredRoles();
  if (roles.includes("ROLE_ADMIN") || roles.includes("ROLE_SALES_ADMIN")) return false;
  return roles.includes("ROLE_SALES_AGENT");
};

/** SALES_ADMIN authority present (Analytics shows every team/agent for these accounts). */
export const isSalesAdminUser = (): boolean => getStoredRoles().includes("ROLE_SALES_ADMIN");

export const SALES_HOME = `${process.env.PUBLIC_URL}/pages/transactions`;

/** Sidebar items sales roles may open: Profiles (Teacher/Student) and Sales. */
export const SALES_MENU_TITLES = ["Profiles", "Profile", "Sales", "Telephony"];

/**
 * Telephony submenus sales roles may open (dialing lives in the bubble). Analytics is open to
 * agents too: getTelephonyAnalyticsScope gives them 'self' (own calls, no pickers).
 */
export const SALES_TELEPHONY_ITEMS = ["Logs", "Countries", "Analytics"];

const SALES_SHARED_PAGES = [
  "student-profile",
  "teacher-profile",
  "AddStudent",
  "teacher-form",
  "transactions",
  "split-payment",
  "sales-agent-performance",
  "telephony-logs",
  "telephony-countries",
  "telephony-analytics",
  "telephony-dialer",
];
/** Visible to SALES_ADMIN and ROLE_ADMIN. A sales agent is sent back to Transactions if they open these URLs. */
const SALES_ADMIN_ONLY_PAGES = ["sales-agents", "sales-targets"];

const toPagePaths = (pages: string[]) =>
  pages.map((page) => `${process.env.PUBLIC_URL}/pages/${page}`);

const matchesSalesPath = (pathname: string, pages: string[]): boolean =>
  toPagePaths(pages).some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const isSalesPath = (pathname: string): boolean => {
  if (matchesSalesPath(pathname, SALES_ADMIN_ONLY_PAGES)) return !isSalesAgentUser();
  return matchesSalesPath(pathname, SALES_SHARED_PAGES);
};

/**
 * Pages outsourced agents may open: Student profile (incl. /:credentialId/:studentId),
 * Telephony Logs (incl. /:callId), Countries, and the Dialer route used by dial links.
 */
const OUT_SOURCING_PATHS = [
  "student-profile",
  "telephony-logs",
  "telephony-countries",
  "telephony-dialer",
].map((page) => `${process.env.PUBLIC_URL}/pages/${page}`);

export const isOutSourcingPath = (pathname: string): boolean =>
  OUT_SOURCING_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`)) ||
  (pathname === OUT_SOURCING_ANALYTICS_PATH && hasOutSourcingAnalyticsAccess());

export const OUT_SOURCING_ANALYTICS_PATH = `${process.env.PUBLIC_URL}/pages/telephony-analytics`;

/**
 * Outsourced agents whose email is on "outSourcingAnalytics" in adminEmailAccess.json
 * also get Telephony → Analytics. UI gate only; the platform scopes the data by token.
 */
export const hasOutSourcingAnalyticsAccess = (): boolean =>
  isOutSourcingUser() && hasEmailAccess("outSourcingAnalytics");
